import { cloneDeep } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, ModalBody, ModalHeader } from 'reactstrap'
import { MODAL_TYPES } from '../../../../common/constants'
import { propertyResponseFields } from '../../../../common/response/property'
import { hideModal } from '../../../../store/modal'
import { findEntityTitle } from '../../../../utils/general'
import RichTextEditor from '../../../RichTextEditor/RichTextEditor'
import ImageCarousel from '../ImageCarousel/ImageCarousel'
import { htmlTemplate1, htmlTemplate2, htmlTemplate3, htmlTemplate4, htmlTemplate5, htmlTemplate6, htmlTemplate7, htmlTemplate8 } from '../templates'
import { ClickedPhotoData, EditedContent, ModalContentProperty, ModalContentProps, TemplatePhoto, TemplateProperty, TemplateProps } from '../types'
import styles from './ShopWindowPrintModal.module.css'

const ModalContent = (props: ModalContentProps) => {
    const { properties, selectedTemplate, isModalVertical } = props

    const [clonedProperties, setClonedProperties] = useState<ModalContentProperty[]>([])
    const [templateData, setTemplateData] = useState('')
    const [templatePhotos, setTemplatePhotos] = useState<TemplatePhoto[]>([])
    const [templateProps, setTemplateProps] = useState<TemplateProps>(null)
    const [carouselOpen, setCarouselOpen] = useState(false)
    const [carouselPhotos, setCarouselPhotos] = useState<TemplatePhoto[]>([])
    const [editedContent, setEditedContent] = useState<EditedContent>(null)
    const [clickedPhotoData, setClickedPhotoData] = useState<ClickedPhotoData | null>(null)
    const [editorInstance, setEditorInstance] = useState<any>(null)
    const [overFlowWarning, setOverFlowWarning] = useState(false)

    const dispatch = useDispatch()
    const { t } = useTranslation()

    const MODAL_VERTICAL_HEIGHT = 1150
    const MODAL_HORIZONTAL_HEIGHT = 850

    const { SHOPWINDOW_PRINT_MODAL } = MODAL_TYPES

    useEffect(() => {
        const clonedProperties = cloneDeep(properties)
        setClonedProperties(clonedProperties)
    }, [properties])

    const selectedId = useMemo(() => clickedPhotoData?.selectedPropertyId, [clickedPhotoData?.selectedPropertyId])

    // handle template & carousel photos
    useEffect(() => {
        const selectedProperties = properties?.length
        // photos - same reference to properties (eg. formattedProperties)
        const photos = properties?.flatMap((property) => property.photos)
        const clonedPhotos = clonedProperties?.flatMap((property) => property.photos)
        const values = [2, 4, 6]

        switch (true) {
            case selectedProperties === 1: {
                setTemplatePhotos(photos)
                setCarouselPhotos(clonedPhotos)
                break
            }
            // 2, 4, or 6 selected properties
            case values.includes(selectedProperties): {
                if (!selectedId) break

                const propertyPhotoBatch = photos.filter((photo) => photo.parent === +selectedId)
                setTemplatePhotos(propertyPhotoBatch)

                const carouselPhotoBatch = clonedPhotos?.filter((photo) => photo.parent === +selectedId)
                setCarouselPhotos(carouselPhotoBatch)
                break
            }
            default:
                break
        }
    }, [properties, clonedProperties, selectedId])

    const slicePhotos = useCallback(
        (endIndex: number) => {
            const props = properties?.map((property) => {
                const { photos, title, phoneNumber, website, ...rest } = property
                // photos - same reference to properties (eg. formattedProperties)
                const slicedPhotos = photos.slice(0, endIndex)
                const backupTitle: string = findEntityTitle(property, propertyResponseFields.PROPERTY_TITLE)

                return {
                    ...rest,
                    title: title ? title : backupTitle,
                    website: website ?? '',
                    phoneNumber: phoneNumber ?? '',
                    photos: slicedPhotos,
                }
            })

            return props
        },
        [properties]
    )

    // handle template props
    useEffect(() => {
        const selectedProperties = properties?.length
        const backupTitle: string = findEntityTitle(properties?.[0], propertyResponseFields.PROPERTY_TITLE)

        if (!templatePhotos) return

        switch (true) {
            case selectedProperties === 1: {
                const property = properties[0]

                const props = {
                    ...property,
                    templatePhotos,
                    title: property.title || backupTitle,
                    service: property.service,
                    type: property.type,
                    website: property.website ?? '',
                    phoneNumber: property.phoneNumber ?? '',
                    overFlowWarning,
                    warning: {
                        title: t('template.warningTitle'),
                        message: t('template.warningMessage'),
                    },
                }
                setTemplateProps(props)
                break
            }
            case selectedProperties === 2: {
                const props = slicePhotos(3)
                setTemplateProps(props)
                break
            }
            case selectedProperties === 4:
            case selectedProperties === 6: {
                const props = slicePhotos(1)
                setTemplateProps(props)
                break
            }
            default:
                break
        }
    }, [properties, templatePhotos, editedContent, overFlowWarning, slicePhotos, t])

    const icons = useMemo(
        () => ({
            id: {
                src: '../images/templates/icon-id.png',
            },
            area: {
                src: '../images/templates/icon-area.png',
            },
            bedrooms: {
                src: '../images/templates/icon-bedrooms.png',
            },
            floor: {
                src: '../images/templates/icon-floor.png',
            },
        }),
        []
    )

    // handle template display
    useEffect(() => {
        if (!templateProps) return

        let template: string

        switch (selectedTemplate) {
            case 'template-1':
                template = htmlTemplate1(templateProps as TemplateProperty)
                setTemplateData(template)
                break
            case 'template-2':
                template = htmlTemplate2(templateProps as TemplateProperty, editedContent)
                setTemplateData(template)
                break
            case 'template-3':
                template = htmlTemplate3(templateProps as TemplateProperty, editedContent)
                setTemplateData(template)
                break
            case 'template-4':
                template = htmlTemplate4(templateProps as ModalContentProperty[], editedContent)
                setTemplateData(template)
                break
            case 'template-5':
                template = htmlTemplate5(templateProps as TemplateProperty, editedContent, icons)
                setTemplateData(template)
                break
            case 'template-6':
                template = htmlTemplate6(templateProps as TemplateProperty, editedContent, icons)
                setTemplateData(template)
                break
            case 'template-7':
                template = htmlTemplate7(templateProps as ModalContentProperty[], editedContent, icons)
                setTemplateData(template)
                break
            case 'template-8':
                template = htmlTemplate8(templateProps as ModalContentProperty[], editedContent, icons)
                setTemplateData(template)
                break
            default:
                console.log(`Template ${selectedTemplate} is not available`)
        }
    }, [templateProps, selectedTemplate, editedContent, icons])

    const handleCarousel = (photoIndex: number, propertyId: number | string) => {
        setCarouselOpen(true)
        setClickedPhotoData({ targetPhotoIndex: photoIndex, selectedPropertyId: propertyId })
    }

    const handleClose = () => dispatch(hideModal({ modalType: SHOPWINDOW_PRINT_MODAL }))

    // tinymce returns html string of edited content
    // when photo changes in template, previously edited content is gone, so this will handle preserve
    const preserveEditedContent = useCallback((content: string) => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(content, 'text/html')

        const matchData = content.match(/template-(\d+)-/)
        if (!matchData) return

        const templateNumber = +matchData[1]

        switch (templateNumber) {
            case 1:
            case 2:
            case 3: {
                const contentClassname = templateNumber === 3 ? '.template-3-content' : '.template-content'
                const templateContent = doc.querySelector(contentClassname)
                const editedContent = templateContent?.outerHTML ?? ''
                setEditedContent(editedContent)
                break
            }
            case 4: {
                const editedTitles: string[] = []
                const titleElements = doc.querySelectorAll('.template-4-title')
                titleElements.forEach((el) => {
                    const title = el.textContent ?? ''
                    editedTitles.push(title)
                })

                const footerElement = doc.querySelector('.template-footer')
                const editedFooter = footerElement?.outerHTML ?? ''

                setEditedContent({ editedTitles, editedFooter })
                break
            }
            case 5:
            case 6: {
                const templateContent = doc.querySelector(`.template-${templateNumber}-content-container`)
                const editedContent = templateContent?.outerHTML ?? ''
                setEditedContent(editedContent)
                break
            }
            case 7:
            case 8: {
                const editedData: string[] = []
                const contentClassname = `.template-${templateNumber}-content`
                const contentElements = doc.querySelectorAll(contentClassname)
                contentElements.forEach((el) => {
                    const editedContent = el.outerHTML
                    editedData.push(editedContent)
                })

                // 7 & 8 share some css
                const footerElement = doc.querySelector('.template-multiple-footer')
                const editedFooter = footerElement?.outerHTML ?? ''

                setEditedContent({ editedData, editedFooter })
                break
            }

            default:
                console.log(`Template ${templateNumber} is not available`)
        }
    }, [])

    const changePhoto = useCallback(
        (e: React.MouseEvent<HTMLImageElement>) => {
            if (!clickedPhotoData) return

            const selectedPhotoId = +e.currentTarget.id
            const { targetPhotoIndex } = clickedPhotoData
            const selectedPhoto = carouselPhotos.find((p) => p.id === selectedPhotoId)

            if (!selectedPhoto) return

            if (editorInstance) {
                const content = editorInstance.getContent()
                preserveEditedContent(content)
            }

            setTemplatePhotos((photos) => {
                const photoToChange = photos[+targetPhotoIndex]

                if (!photoToChange) return photos

                // photos - same reference to properties (eg. formattedProperties)
                photoToChange.id = selectedPhoto.id
                photoToChange.src = selectedPhoto.src

                return [...photos]
            })
        },
        [clickedPhotoData, carouselPhotos, editorInstance, preserveEditedContent]
    )

    const editorHeight = useMemo(
        () => (isModalVertical ? MODAL_VERTICAL_HEIGHT : MODAL_HORIZONTAL_HEIGHT),
        [isModalVertical, MODAL_VERTICAL_HEIGHT, MODAL_HORIZONTAL_HEIGHT]
    )

    return (
        <>
            <div className={styles.modalHeaderContainer}>
                <ModalHeader>{t('modal.shopWindowPrint.title')}</ModalHeader>
                <div>
                    <Button type="button" color="send" className="btn-label me-2" onClick={() => editorInstance?.execCommand('mcePrint')}>
                        <i className="mdi mdi-printer-outline align-bottom label-icon align-middle fs-16 me-2"></i>
                        {t('app.common.print')}
                    </Button>
                    <Button type="button" color="primary" className="btn-label" data-bs-dismiss="modal" onClick={handleClose}>
                        <i className="mdi mdi-close label-icon align-middle fs-16"></i>
                        {t('button.cancel')}
                    </Button>
                </div>
            </div>
            <ModalBody>
                {carouselOpen && (
                    <>
                        <div className={styles.btnCloseContainer}>
                            <Button
                                type="button"
                                className={styles.btnClose}
                                onClick={() => {
                                    setCarouselOpen(false)
                                    setClickedPhotoData(null)
                                }}
                            >
                                <i className="mdi mdi-close fs-16"></i>
                            </Button>
                        </div>
                        <ImageCarousel photos={carouselPhotos} changePhoto={changePhoto} />
                    </>
                )}
                <RichTextEditor
                    content={templateData}
                    height={editorHeight}
                    onEditorInit={setEditorInstance}
                    handleCarousel={handleCarousel}
                    setOverFlowWarning={setOverFlowWarning}
                />
            </ModalBody>
        </>
    )
}

export default ModalContent
